import api from '@/lib/api';
import { ROUTES } from '@/constants/routes';
import { ClientsExportOptions } from './types';

export const exportClients = async (options: ClientsExportOptions): Promise<void> => {
  const { format, filename, includeContacts = true, includeVatInfo = true } = options;
  
  try {
    // Determine the endpoint based on format
    const endpoint = getExportEndpoint(format);
    
    // Prepare query parameters
    const params = new URLSearchParams();
    if (includeContacts) params.append('include_contacts', 'true');
    if (includeVatInfo) params.append('include_vat_info', 'true');
    if (options.dateRange) {
      params.append('date_from', options.dateRange.from.toISOString().split('T')[0]);
      params.append('date_to', options.dateRange.to.toISOString().split('T')[0]);
    }
    
    const url = `${endpoint}?${params.toString()}`;
    
    const response = await api.get(url, {
      responseType: 'blob',
    });
    
    // Create download link
    const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', filename || getDefaultFilename(format));
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
    
  } catch (error) {
    console.error('Export error:', error);
    throw new Error(`Failed to export clients as ${format.toUpperCase()}`);
  }
};

export const exportClientsPdf = async (options?: Partial<ClientsExportOptions>): Promise<void> => {
  await exportClients({
    format: 'pdf',
    ...options,
  });
};

export const exportClientsExcel = async (options?: Partial<ClientsExportOptions>): Promise<void> => {
  await exportClients({
    format: 'excel',
    ...options,
  });
};

export const exportClientsCsv = async (options?: Partial<ClientsExportOptions>): Promise<void> => {
  await exportClients({
    format: 'csv',
    ...options,
  });
};

// Helper functions
function getExportEndpoint(format: 'pdf' | 'excel' | 'csv'): string {
  const baseEndpoint = ROUTES.API.CLIENTS;
  
  switch (format) {
    case 'pdf':
      return `${baseEndpoint}export_pdf/`;
    case 'excel':
      return `${baseEndpoint}export_excel/`;
    case 'csv':
      return `${baseEndpoint}export_csv/`;
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
}

function getDefaultFilename(format: 'pdf' | 'excel' | 'csv'): string {
  const timestamp = new Date().toISOString().split('T')[0];
  
  switch (format) {
    case 'pdf':
      return `clients_${timestamp}.pdf`;
    case 'excel':
      return `clients_${timestamp}.xlsx`;
    case 'csv':
      return `clients_${timestamp}.csv`;
    default:
      return `clients_${timestamp}.${format}`;
  }
}

// Utility to format export data for custom exports
export const formatClientForExport = (client: any, options: Partial<ClientsExportOptions> = {}) => {
  const { includeContacts = true, includeVatInfo = true } = options;
  
  const baseData = {
    'Company Name': client.name,
    'Friendly Name': client.friendly_name || '',
    'Email': client.email,
    'Address': client.address,
    'Country': client.country,
  };
  
  if (includeVatInfo) {
    Object.assign(baseData, {
      'VAT Number': client.vat_number || '',
      'VAT Setting': client.vat_setting,
      'VAT Rate': client.vat_setting === 'override' ? `${client.vat_rate}%` : '',
      'VAT Override Message': client.vat_override_message || '',
    });
  }
  
  if (includeContacts) {
    // Add contact persons
    const contactPersons = client.contacts?.map((contact: any, index: number) => ({
      [`Contact ${index + 1} Name`]: contact.name,
      [`Contact ${index + 1} Email`]: contact.email,
    })).reduce((acc: any, contact: any) => ({ ...acc, ...contact }), {}) || {};
    
    Object.assign(baseData, contactPersons);
    
    // Add client contacts count
    Object.assign(baseData, {
      'Internal Contacts Count': client.client_contacts?.length || 0,
    });
  }
  
  return baseData;
};

// Utility to create custom CSV export
export const createCsvExport = (clients: any[], options: Partial<ClientsExportOptions> = {}): string => {
  if (clients.length === 0) return '';
  
  const formattedData = clients.map(client => formatClientForExport(client, options));
  const headers = Object.keys(formattedData[0]);
  
  const csvContent = [
    headers.join(','),
    ...formattedData.map(row => 
      headers.map(header => {
        const value = (row as any)[header]?.toString() || '';
        // Escape commas and quotes in CSV
        return value.includes(',') || value.includes('"') 
          ? `"${value.replace(/"/g, '""')}"` 
          : value;
      }).join(',')
    )
  ].join('\n');
  
  return csvContent;
};

// Utility to download custom CSV
export const downloadCsv = (csvContent: string, filename?: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename || getDefaultFilename('csv'));
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};
